import { createFileRoute, useParams, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import {
  emptyEvaluation,
  evaluateRecommendation,
  READINESS_DESCRIPTIONS,
  type AdmissionRules,
  type EvaluationData,
} from "@/lib/prepare-evaluation";
import { ArrowLeft, Save, Download, Printer, Eye, Plus, Trash2, Sparkles, CheckCircle2, XCircle, PauseCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/utvardering/$id")({
  head: () => ({
    meta: [{ title: "Prepare-utvärdering — Inkubera" }],
  }),
  component: UtvarderingDetail,
});

type Prospect = {
  id: string;
  case_name: string;
  org_number: string | null;
  contact_name: string | null;
  contact_email: string | null;
  municipality: string | null;
  status: string;
  registration_date: string | null;
  company_id: string | null;
};

function UtvarderingDetail() {
  const { id } = useParams({ from: "/utvardering/$id" });
  const { user } = useAuth();
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [evalId, setEvalId] = useState<string | null>(null);
  const [data, setData] = useState<EvaluationData>(emptyEvaluation());
  const [date, setDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [performedBy, setPerformedBy] = useState("");
  const [rules, setRules] = useState<AdmissionRules>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<"form" | "report">("form");
  const [exporting, setExporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      const { data: p } = await supabase.from("prospects").select("*").eq("id", id).single();
      if (p) {
        setProspect(p as Prospect);
        if (p.company_id) setCompanyId(p.company_id);
        setData((d) => ({
          ...d,
          fakta: {
            ...d.fakta,
            case_name: p.case_name,
            org_number: p.org_number ?? "",
            registration_date: p.registration_date ?? "",
            municipality_text: p.municipality ?? "",
            contacts:
              p.contact_name
                ? [{ name: p.contact_name, role: "" }]
                : d.fakta.contacts,
          },
        }));
      }
      const { data: ev } = await supabase
        .from("prepare_evaluations")
        .select("*")
        .eq("prospect_id", id)
        .maybeSingle();
      if (ev) {
        setEvalId(ev.id);
        setData(ev.data as EvaluationData);
        setDate(ev.evaluation_date);
        setPerformedBy(ev.performed_by_name);
      } else if (user) {
        const { data: prof } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", user.id)
          .maybeSingle();
        setPerformedBy(prof?.full_name ?? user.email ?? "");
      }
      const { data: cr } = await supabase
        .from("prepare_admission_criteria")
        .select("rules")
        .eq("active", true)
        .maybeSingle();
      if (cr?.rules) setRules(cr.rules as AdmissionRules);
      setLoading(false);
    })();
  }, [id, user]);

  const recommendation = useMemo(() => evaluateRecommendation(data, rules), [data, rules]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    // Spegla några nyckelfält tillbaka till prospect
    await supabase
      .from("prospects")
      .update({
        case_name: data.fakta.case_name || prospect?.case_name || "Namnlöst case",
        org_number: data.fakta.org_number || null,
        registration_date: data.fakta.registration_date || null,
        municipality: data.fakta.municipality_text || null,
        contact_name: data.fakta.contacts[0]?.name || null,
      })
      .eq("id", id);

    const payload = {
      prospect_id: id,
      evaluation_date: date,
      performed_by_name: performedBy,
      performed_by_user: user.id,
      data,
      recommendation: recommendation.decision,
      recommendation_reasons: { reasons: recommendation.reasons, passed: recommendation.passed },
      recommendation_generated_at: new Date().toISOString(),
    };
    if (evalId) {
      await supabase.from("prepare_evaluations").update(payload).eq("id", evalId);
    } else {
      const { data: ins } = await supabase
        .from("prepare_evaluations")
        .insert(payload)
        .select("id")
        .single();
      if (ins) setEvalId(ins.id);
    }
    setSaving(false);
  };

  const [companyId, setCompanyId] = useState<string | null>(null);

  const setProspectStatus = async (status: "godkänd" | "avslagen" | "parkerad" | "utvärdering") => {
    if (!prospect || !user) return;
    const label =
      status === "godkänd"
        ? "Markera som godkänd, skapa bolag i portföljen och flytta till förinkubation?"
        : status === "avslagen"
          ? "Markera som avslagen?"
          : status === "parkerad"
            ? "Parkera detta case?"
            : "Återställ till utvärdering?";
    if (!confirm(label)) return;

    let newCompanyId: string | null = prospect.company_id ?? companyId;

    if (status === "godkänd" && !newCompanyId) {
      // Skapa bolag och länka
      const { data: comp, error: compErr } = await supabase
        .from("companies")
        .insert({
          name: data.fakta.case_name || prospect.case_name,
          org_number: data.fakta.org_number || prospect.org_number || null,
          municipality: data.fakta.municipality_text || prospect.municipality || null,
          contact_name: data.fakta.contacts[0]?.name || prospect.contact_name || null,
          contact_email: prospect.contact_email || null,
          fas: "Förinkubation",
          status: "Aktivt",
          source_prospect_id: prospect.id,
          created_by: user.id,
        })
        .select("id")
        .single();
      if (compErr) {
        alert(`Kunde inte skapa bolag: ${compErr.message}`);
        return;
      }
      newCompanyId = comp.id;
      setCompanyId(comp.id);
    }

    const { error } = await supabase
      .from("prospects")
      .update({ status, company_id: newCompanyId })
      .eq("id", id);
    if (error) {
      alert(error.message);
      return;
    }
    setProspect({ ...prospect, status, company_id: newCompanyId });
  };


  const buildPdf = async () => {
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import("jspdf"),
      import("html2canvas-pro"),
    ]);
    const node = reportRef.current!;
    const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
    const imgData = canvas.toDataURL("image/jpeg", 0.95);
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW;
    const imgH = (canvas.height * imgW) / canvas.width;
    let heightLeft = imgH;
    let position = 0;
    pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
    heightLeft -= pageH;
    while (heightLeft > 0) {
      position = heightLeft - imgH;
      pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
      heightLeft -= pageH;
    }
    return pdf;
  };

  const handleExportPDF = async () => {
    if (view !== "report") setView("report");
    await new Promise((r) => setTimeout(r, 100));
    setExporting(true);
    try {
      const pdf = await buildPdf();
      pdf.save(`prepare-utvardering-${data.fakta.case_name || "case"}.pdf`);
    } finally {
      setExporting(false);
    }
  };

  const handlePreviewPDF = async () => {
    if (view !== "report") setView("report");
    await new Promise((r) => setTimeout(r, 100));
    setPreviewing(true);
    try {
      const pdf = await buildPdf();
      const url = URL.createObjectURL(pdf.output("blob"));
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setPreviewOpen(true);
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="p-8 text-muted-foreground">Laddar…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-5xl">
        <div className="flex items-center justify-between gap-4 mb-6 print:hidden">
          <Link
            to="/utvardering"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" /> Tillbaka
          </Link>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg bg-secondary p-1">
              <button
                onClick={() => setView("form")}
                className={`px-3 py-1.5 text-xs rounded-md ${view === "form" ? "bg-background shadow" : ""}`}
              >
                Formulär
              </button>
              <button
                onClick={() => setView("report")}
                className={`px-3 py-1.5 text-xs rounded-md ${view === "report" ? "bg-background shadow" : ""}`}
              >
                Rapportvy
              </button>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="h-9 px-3 rounded-lg bg-foreground text-background text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="size-4" /> {saving ? "Sparar…" : "Spara"}
            </button>
            <button
              onClick={() => window.print()}
              className="h-9 px-3 rounded-lg bg-secondary text-sm inline-flex items-center gap-2"
            >
              <Printer className="size-4" /> Skriv ut
            </button>
            <button
              onClick={handlePreviewPDF}
              disabled={previewing}
              className="h-9 px-3 rounded-lg bg-secondary text-sm inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Eye className="size-4" /> {previewing ? "…" : "Förhandsgranska"}
            </button>
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              className="h-9 px-3 rounded-lg bg-foreground text-background text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Download className="size-4" /> {exporting ? "…" : "PDF"}
            </button>
          </div>
        </div>

        {prospect && (
          <div className="mb-6 rounded-2xl border border-border bg-card p-4 flex flex-wrap items-center justify-between gap-3 print:hidden">
            <div className="flex items-center gap-3 text-sm flex-wrap">
              <span className="text-muted-foreground">Status:</span>
              <StatusPill status={prospect.status} />
              {companyId && (
                <Link
                  to="/bolag/$id"
                  params={{ id: companyId }}
                  className="text-xs px-2.5 py-1 rounded-full bg-secondary hover:bg-secondary/70 inline-flex items-center gap-1.5"
                >
                  Öppna bolagskort →
                </Link>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setProspectStatus("godkänd")}
                disabled={prospect.status === "godkänd"}
                className="h-9 px-3 rounded-lg bg-green-600/15 text-green-700 dark:text-green-300 text-xs font-medium inline-flex items-center gap-1.5 hover:bg-green-600/25 disabled:opacity-50"
              >
                <CheckCircle2 className="size-3.5" /> Godkänn → Förinkubation
              </button>
              <button
                onClick={() => setProspectStatus("avslagen")}
                disabled={prospect.status === "avslagen"}
                className="h-9 px-3 rounded-lg bg-red-600/15 text-red-700 dark:text-red-300 text-xs font-medium inline-flex items-center gap-1.5 hover:bg-red-600/25 disabled:opacity-50"
              >
                <XCircle className="size-3.5" /> Avslå
              </button>
              <button
                onClick={() => setProspectStatus("parkerad")}
                disabled={prospect.status === "parkerad"}
                className="h-9 px-3 rounded-lg bg-secondary text-xs font-medium inline-flex items-center gap-1.5 hover:bg-secondary/80 disabled:opacity-50"
              >
                <PauseCircle className="size-3.5" /> Parkera
              </button>
              {prospect.status !== "utvärdering" && (
                <button
                  onClick={() => setProspectStatus("utvärdering")}
                  className="h-9 px-3 rounded-lg bg-secondary text-xs inline-flex items-center gap-1.5 hover:bg-secondary/80"
                >
                  Återställ
                </button>
              )}
            </div>
          </div>
        )}

        {view === "form" ? (
          <FormView
            data={data}
            setData={setData}
            date={date}
            setDate={setDate}
            performedBy={performedBy}
            setPerformedBy={setPerformedBy}
            recommendation={recommendation}
            rules={rules}
          />
        ) : (
          <div ref={reportRef} className="bg-background">
            <ReportView
              data={data}
              date={date}
              performedBy={performedBy}
              recommendation={recommendation}
            />
          </div>
        )}
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Förhandsvisning</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <iframe
              src={previewUrl}
              className="w-full h-[70vh] rounded-lg border border-border"
              title="PDF-förhandsvisning"
            />
          )}
          <DialogFooter>
            <button
              onClick={() => {
                if (!previewUrl) return;
                const a = document.createElement("a");
                a.href = previewUrl;
                a.download = `prepare-utvardering-${data.fakta.case_name || "case"}.pdf`;
                a.click();
              }}
              className="h-9 px-4 rounded-lg bg-foreground text-background text-sm inline-flex items-center gap-2"
            >
              <Download className="size-4" /> Ladda ner
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

// =============== FORMULÄR ===============

type FormProps = {
  data: EvaluationData;
  setData: (fn: (prev: EvaluationData) => EvaluationData) => void;
  date: string;
  setDate: (v: string) => void;
  performedBy: string;
  setPerformedBy: (v: string) => void;
  recommendation: ReturnType<typeof evaluateRecommendation>;
  rules: AdmissionRules;
};

function FormView(p: FormProps) {
  const { data, setData } = p;
  return (
    <div className="space-y-8">
      <RecommendationCard rec={p.recommendation} rules={p.rules} />

      <Section title="Utvärdering" desc="Datum och utförare.">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Datum">
            <input
              type="date"
              value={p.date}
              onChange={(e) => p.setDate(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Utvärdering utförd av">
            <input
              value={p.performedBy}
              onChange={(e) => p.setPerformedBy(e.target.value)}
              className="input"
            />
          </Field>
        </div>
      </Section>

      <Section title="Fakta">
        <Field label="Namn på case/företag">
          <input
            value={data.fakta.case_name}
            onChange={(e) => setData((d) => ({ ...d, fakta: { ...d.fakta, case_name: e.target.value } }))}
            className="input"
          />
        </Field>
        <Field label="Kontaktperson(er) (krav på minst en aktiv inför Prepare)">
          <div className="space-y-2">
            {data.fakta.contacts.map((c, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <input
                  placeholder="Förnamn Efternamn"
                  value={c.name}
                  onChange={(e) =>
                    setData((d) => {
                      const arr = [...d.fakta.contacts];
                      arr[i] = { ...arr[i], name: e.target.value };
                      return { ...d, fakta: { ...d.fakta, contacts: arr } };
                    })
                  }
                  className="input"
                />
                <input
                  placeholder="Roll"
                  value={c.role}
                  onChange={(e) =>
                    setData((d) => {
                      const arr = [...d.fakta.contacts];
                      arr[i] = { ...arr[i], role: e.target.value };
                      return { ...d, fakta: { ...d.fakta, contacts: arr } };
                    })
                  }
                  className="input"
                />
                <button
                  onClick={() =>
                    setData((d) => ({
                      ...d,
                      fakta: { ...d.fakta, contacts: d.fakta.contacts.filter((_, j) => j !== i) },
                    }))
                  }
                  className="px-2 text-muted-foreground hover:text-destructive"
                  type="button"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setData((d) => ({
                  ...d,
                  fakta: { ...d.fakta, contacts: [...d.fakta.contacts, { name: "", role: "" }] },
                }))
              }
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <Plus className="size-3" /> Lägg till kontakt
            </button>
          </div>
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Förankring kommun/Örebro län">
            <select
              value={data.fakta.municipality_anchor}
              onChange={(e) =>
                setData((d) => ({
                  ...d,
                  fakta: { ...d.fakta, municipality_anchor: e.target.value as "ja" | "nej" | "" },
                }))
              }
              className="input"
            >
              <option value="">— välj —</option>
              <option value="ja">Ja</option>
              <option value="nej">Nej</option>
            </select>
          </Field>
          <Field label="Kommun/län (kommentar)">
            <input
              value={data.fakta.municipality_text}
              onChange={(e) =>
                setData((d) => ({ ...d, fakta: { ...d.fakta, municipality_text: e.target.value } }))
              }
              className="input"
            />
          </Field>
          <Field label="Organisationsnummer">
            <input
              value={data.fakta.org_number}
              onChange={(e) => setData((d) => ({ ...d, fakta: { ...d.fakta, org_number: e.target.value } }))}
              className="input"
            />
          </Field>
          <Field label="Företagets registreringsdatum">
            <input
              type="date"
              value={data.fakta.registration_date}
              onChange={(e) =>
                setData((d) => ({ ...d, fakta: { ...d.fakta, registration_date: e.target.value } }))
              }
              className="input"
            />
          </Field>
        </div>
      </Section>

      <Section title="Grundkriterier" desc="Krav som behöver bedömas inför Prepare.">
        <YesNoField
          label="Fristående AB?"
          help="Krav på registrerat fristående AB (kontroll av bolagskonstruktion/ägarbild)."
          value={data.grund.fristaende_ab.value}
          comment={data.grund.fristaende_ab.comment}
          onChange={(value, comment) =>
            setData((d) => ({ ...d, grund: { ...d.grund, fristaende_ab: { value, comment } } }))
          }
          allowNotRelevant
        />
        <YesNoField
          label="Godkänd för statsstöd (Art.22 GBER eller försumbart stöd)?"
          help="Svenskt registrerat AB yngre än 5 år, ej tagit över annan verksamhet, ej börsnoterat, ej delat ut vinst, ej stor omsättning. Försumbart stöd: max 200 000 € senaste 3 åren."
          value={data.grund.statsstod_godkand.value}
          comment={data.grund.statsstod_godkand.comment}
          onChange={(value, comment) =>
            setData((d) => ({ ...d, grund: { ...d.grund, statsstod_godkand: { value, comment } } }))
          }
          allowNotRelevant
        />
        <Field label="Företaget ska inte vara marknadsredo">
          <select
            value={data.grund.ej_marknadsredo.value}
            onChange={(e) =>
              setData((d) => ({
                ...d,
                grund: {
                  ...d.grund,
                  ej_marknadsredo: { ...d.grund.ej_marknadsredo, value: e.target.value as "ok" | "ej_ok" | "" },
                },
              }))
            }
            className="input"
          >
            <option value="">— välj —</option>
            <option value="ok">Ok, ej marknadsredo</option>
            <option value="ej_ok">Ej ok</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Eget kapital understiger omkostnader nästa 24 mån; affärsmodell ej validerad; ingen återkommande
            försäljning.
          </p>
          <textarea
            placeholder="Kommentarer"
            value={data.grund.ej_marknadsredo.comment}
            onChange={(e) =>
              setData((d) => ({
                ...d,
                grund: { ...d.grund, ej_marknadsredo: { ...d.grund.ej_marknadsredo, comment: e.target.value } },
              }))
            }
            className="input mt-2 min-h-16"
          />
        </Field>
        <YesNoField
          label="Grundarägt och grundardrivet företag?"
          help="Inkuberas/Vinnovas medel ska göra grundarägda och grundardrivna företag marknadsredo."
          value={data.grund.grundarteam.value}
          comment={data.grund.grundarteam.comment}
          onChange={(value, comment) =>
            setData((d) => ({ ...d, grund: { ...d.grund, grundarteam: { value, comment } } }))
          }
        />

        <Field label="Grundarteam (uppdaterad översikt)">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs">
                <tr>
                  <th className="text-left p-2">Namn / ägarstruktur</th>
                  <th className="text-left p-2 w-32">Ägarandel %</th>
                  <th className="text-left p-2 w-36">Engagemang %</th>
                  <th className="text-left p-2">Roll</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {data.grund.team.map((m, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-2">
                      <input
                        value={m.name}
                        onChange={(e) =>
                          setData((d) => {
                            const arr = [...d.grund.team];
                            arr[i] = { ...arr[i], name: e.target.value };
                            return { ...d, grund: { ...d.grund, team: arr } };
                          })
                        }
                        className="input"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        value={m.ownership}
                        onChange={(e) =>
                          setData((d) => {
                            const arr = [...d.grund.team];
                            arr[i] = { ...arr[i], ownership: e.target.value };
                            return { ...d, grund: { ...d.grund, team: arr } };
                          })
                        }
                        className="input"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        value={m.engagement}
                        onChange={(e) =>
                          setData((d) => {
                            const arr = [...d.grund.team];
                            arr[i] = { ...arr[i], engagement: e.target.value };
                            return { ...d, grund: { ...d.grund, team: arr } };
                          })
                        }
                        className="input"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        value={m.role}
                        onChange={(e) =>
                          setData((d) => {
                            const arr = [...d.grund.team];
                            arr[i] = { ...arr[i], role: e.target.value };
                            return { ...d, grund: { ...d.grund, team: arr } };
                          })
                        }
                        className="input"
                      />
                    </td>
                    <td className="p-2 text-right">
                      <button
                        type="button"
                        onClick={() =>
                          setData((d) => ({
                            ...d,
                            grund: { ...d.grund, team: d.grund.team.filter((_, j) => j !== i) },
                          }))
                        }
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            onClick={() =>
              setData((d) => ({
                ...d,
                grund: {
                  ...d.grund,
                  team: [...d.grund.team, { name: "", ownership: "", engagement: "", role: "" }],
                },
              }))
            }
            className="mt-2 text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <Plus className="size-3" /> Lägg till teammedlem
          </button>
        </Field>

        <Field label="Eventuella kompletterande ägare">
          <textarea
            value={data.grund.extra_owners}
            onChange={(e) => setData((d) => ({ ...d, grund: { ...d.grund, extra_owners: e.target.value } }))}
            className="input min-h-16"
          />
        </Field>
      </Section>

      <Section title="Företags-/personkontroll" desc="Bakgrundskontroll utförd av Inkuberas verksamhetscontroller.">
        <Field label="Bakgrundskontroll">
          <select
            value={data.bg_check.value}
            onChange={(e) =>
              setData((d) => ({
                ...d,
                bg_check: { ...d.bg_check, value: e.target.value as "ok" | "ej_ok" | "" },
              }))
            }
            className="input"
          >
            <option value="">— välj —</option>
            <option value="ok">Ok</option>
            <option value="ej_ok">Ej ok</option>
          </select>
          <textarea
            placeholder="Kommentarer"
            value={data.bg_check.comment}
            onChange={(e) =>
              setData((d) => ({ ...d, bg_check: { ...d.bg_check, comment: e.target.value } }))
            }
            className="input mt-2 min-h-16"
          />
        </Field>
      </Section>

      <Section title="Nulägesbeskrivning" desc="Fylls i kortfattat och kärnfullt.">
        <TextField
          label="Affärsidé"
          help="Innovativ/ny lösning på ett viktigt problem/behov hos en identifierad kundgrupp."
          value={data.nulage.affarside}
          onChange={(v) => setData((d) => ({ ...d, nulage: { ...d.nulage, affarside: v } }))}
        />
        <TextField
          label="Idéns ursprung"
          help="Obs ev. arbetsgivare-intressen m.m."
          value={data.nulage.ide_ursprung}
          onChange={(v) => setData((d) => ({ ...d, nulage: { ...d.nulage, ide_ursprung: v } }))}
        />
        <TextField
          label="Idébärare/teamets befintliga kompetenser"
          help="T.ex. relevant bakgrund, sälj/affärer, teknisk utveckling, bolagsbyggande, finansiering."
          value={data.nulage.kompetenser}
          onChange={(v) => setData((d) => ({ ...d, nulage: { ...d.nulage, kompetenser: v } }))}
        />
        <TextField
          label="Status IP"
          help="Upphovsrätt, patent, källkod, varumärke, designskydd, avtal. Vem äger vad?"
          value={data.nulage.ip_status}
          onChange={(v) => setData((d) => ({ ...d, nulage: { ...d.nulage, ip_status: v } }))}
        />
        <TextField
          label="Var står projektet/företaget i sin utveckling idag?"
          help="Idéstadiet / Testversion / Säljbar version."
          value={data.nulage.utveckling}
          onChange={(v) => setData((d) => ({ ...d, nulage: { ...d.nulage, utveckling: v } }))}
        />
        <TextField
          label="Time to market (MVP)"
          help="Första uppskattning av första säljbara version (t.ex. 6 mån, 1 år, 2 år)."
          value={data.nulage.time_to_market}
          onChange={(v) => setData((d) => ({ ...d, nulage: { ...d.nulage, time_to_market: v } }))}
        />
        <TextField
          label="Behov"
          help="Casets/företagets viktigaste behov och utmaningar."
          value={data.nulage.behov}
          onChange={(v) => setData((d) => ({ ...d, nulage: { ...d.nulage, behov: v } }))}
        />
      </Section>

      <Section
        title="Mognadsgrad — Readiness Levels"
        desc="Inga nivåkrav vid antagning till Prepare, men används som underlag."
      >
        {(["crl", "brl", "tmrl", "srl"] as const).map((k) => (
          <ReadinessRow
            key={k}
            keyName={k}
            value={data.readiness[k]}
            onChange={(v) =>
              setData((d) => ({ ...d, readiness: { ...d.readiness, [k]: v } }))
            }
          />
        ))}
      </Section>

      <Section
        title="Bedömningskriterier — Affärspotential"
        desc="Värde 0-3: 3=starkt case, 2=fungerande, 1=svag men utvecklingsbar, 0=ej godkänd."
      >
        {(
          [
            { k: "innovativt", label: "Innovativt", desc: "Unika egenskaper som ger uthålliga konkurrensfördelar." },
            { k: "hallbar", label: "Hållbarhetsdriven affär", desc: "Löser samhälls-/marknadsutmaning på ett ekonomiskt och socialt hållbart sätt." },
            { k: "skalbar", label: "Skalbarhet i affärsmodellen", desc: "Bedömning av tillväxtpotential." },
            { k: "unika_kunskaper", label: "Unika kunskapstillgångar", desc: "Kunskap som ger långsiktigt försprång." },
            { k: "team_bedomning", label: "Person/teambedömning samt kompetens/engagemang", desc: "Förmåga att genomföra Prepare-fasen tillfredsställande." },
          ] as const
        ).map(({ k, label, desc }) => (
          <AssessmentRow
            key={k}
            label={label}
            help={desc}
            value={data.bedomning[k]}
            onChange={(v) =>
              setData((d) => ({ ...d, bedomning: { ...d.bedomning, [k]: v } }))
            }
          />
        ))}
      </Section>

      <Section title="Företagskategori" desc="Flera alternativ möjliga.">
        {(
          [
            { k: "kunskapsintensivt", label: "Kunskapsintensivt företag" },
            { k: "hi_tech", label: "Hi-tech företag" },
            { k: "deeptech", label: "Deeptech företag" },
            { k: "ingen", label: "Ingen av dessa kategorier" },
          ] as const
        ).map(({ k, label }) => (
          <CategoryRow
            key={k}
            label={label}
            value={data.kategori[k]}
            onChange={(v) =>
              setData((d) => ({ ...d, kategori: { ...d.kategori, [k]: v } }))
            }
          />
        ))}
      </Section>

      <Section
        title="Vinnovas speciella riktlinjer"
        desc="Vilken kategori bedöms affärscaset/företaget tillhöra?"
      >
        {(
          [
            { k: "digital_konsult", label: "Digital konsult- eller tjänsteaffär" },
            { k: "affarskoncept_utan_teknik", label: "Affärskoncept utan tydlig teknisk lösning" },
            { k: "teknikkoncept_utan_affar", label: "Teknikkoncept utan tydlig affär" },
            { k: "genomforande", label: "Genomförandeaffär (Execution Business)" },
            { k: "uppfinnare", label: "Uppfinnare/utvecklingsbolag" },
            { k: "socialt", label: "Socialt eller samhällsinriktade företag" },
            { k: "spelstudio", label: "Spelstudios" },
            { k: "mode_artist", label: "Mode, artist, etc" },
            { k: "ingen", label: "Ingen av dessa kategorier passar in" },
          ] as const
        ).map(({ k, label }) => (
          <CategoryRow
            key={k}
            label={label}
            value={data.vinnova[k]}
            onChange={(v) =>
              setData((d) => ({ ...d, vinnova: { ...d.vinnova, [k]: v } }))
            }
          />
        ))}
      </Section>

      <Section title="Beslut om antagning Prepare-programmet">
        <TextField
          label="Eventuella kommentarer"
          value={data.beslut.kommentar}
          onChange={(v) => setData((d) => ({ ...d, beslut: { ...d.beslut, kommentar: v } }))}
        />
        <TextField
          label="Beslut"
          value={data.beslut.beslut_text}
          onChange={(v) => setData((d) => ({ ...d, beslut: { ...d.beslut, beslut_text: v } }))}
        />
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Affärscoach för bolagsuppdrag">
            <input
              value={data.beslut.affarscoach_namn}
              onChange={(e) =>
                setData((d) => ({ ...d, beslut: { ...d.beslut, affarscoach_namn: e.target.value } }))
              }
              className="input"
            />
          </Field>
          <Field label="Kontroll utförd">
            <input
              type="date"
              value={data.beslut.affarscoach_kontroll_datum}
              onChange={(e) =>
                setData((d) => ({
                  ...d,
                  beslut: { ...d.beslut, affarscoach_kontroll_datum: e.target.value },
                }))
              }
              className="input"
            />
          </Field>
        </div>
        <YesNoField
          label="Ingen jäv-situation finns"
          help="Coach är ej styrelsemedlem/delägare i aktuellt bolag."
          value={data.beslut.jav_ok.value}
          comment={data.beslut.jav_ok.comment}
          onChange={(value, comment) =>
            setData((d) => ({ ...d, beslut: { ...d.beslut, jav_ok: { value, comment } } }))
          }
        />
      </Section>
    </div>
  );
}

// =============== RAPPORTVY (för PDF/print) ===============

function ReportView({
  data,
  date,
  performedBy,
  recommendation,
}: {
  data: EvaluationData;
  date: string;
  performedBy: string;
  recommendation: ReturnType<typeof evaluateRecommendation>;
}) {
  return (
    <div className="bg-white text-neutral-900 p-10 print:p-0 space-y-6 text-[13px] leading-relaxed">
      <div className="border-b border-neutral-300 pb-4">
        <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-1">Inkubera</div>
        <h1 className="font-display text-2xl">
          Utvärdering inför Prepare: {data.fakta.case_name || "—"}
        </h1>
        <p className="text-xs text-neutral-600 mt-2">
          Internt beslutsunderlag inför Prepare. Datum: {date} · Utförd av: {performedBy || "—"}
        </p>
      </div>

      <RecBlock rec={recommendation} />

      <ReportSection title="Fakta">
        <KV k="Case/företag" v={data.fakta.case_name} />
        <KV
          k="Kontaktperson(er)"
          v={data.fakta.contacts.filter((c) => c.name).map((c) => `${c.name}${c.role ? ` (${c.role})` : ""}`).join(", ")}
        />
        <KV
          k="Förankring kommun/Örebro län"
          v={`${data.fakta.municipality_anchor || "—"}${data.fakta.municipality_text ? ` — ${data.fakta.municipality_text}` : ""}`}
        />
        <KV k="Organisationsnummer" v={data.fakta.org_number} />
        <KV k="Registreringsdatum" v={data.fakta.registration_date} />
      </ReportSection>

      <ReportSection title="Grundkriterier">
        <KV k="Fristående AB" v={fmtYesNo(data.grund.fristaende_ab)} />
        <KV k="Statsstöd godkänd" v={fmtYesNo(data.grund.statsstod_godkand)} />
        <KV
          k="Ej marknadsredo"
          v={`${data.grund.ej_marknadsredo.value || "—"}${data.grund.ej_marknadsredo.comment ? ` — ${data.grund.ej_marknadsredo.comment}` : ""}`}
        />
        <KV k="Grundarägt/grundardrivet" v={fmtYesNo(data.grund.grundarteam)} />
        {data.grund.team.some((m) => m.name) && (
          <div className="mt-2">
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Grundarteam</div>
            <table className="w-full text-xs border border-neutral-300">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="text-left p-2">Namn</th>
                  <th className="text-left p-2 w-24">Ägarandel</th>
                  <th className="text-left p-2 w-28">Engagemang</th>
                  <th className="text-left p-2">Roll</th>
                </tr>
              </thead>
              <tbody>
                {data.grund.team
                  .filter((m) => m.name)
                  .map((m, i) => (
                    <tr key={i} className="border-t border-neutral-300">
                      <td className="p-2">{m.name}</td>
                      <td className="p-2">{m.ownership}</td>
                      <td className="p-2">{m.engagement}</td>
                      <td className="p-2">{m.role}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
        {data.grund.extra_owners && <KV k="Kompletterande ägare" v={data.grund.extra_owners} />}
      </ReportSection>

      <ReportSection title="Företags-/personkontroll">
        <KV
          k="Bakgrundskontroll"
          v={`${data.bg_check.value || "—"}${data.bg_check.comment ? ` — ${data.bg_check.comment}` : ""}`}
        />
      </ReportSection>

      <ReportSection title="Nulägesbeskrivning">
        <KVBlock k="Affärsidé" v={data.nulage.affarside} />
        <KVBlock k="Idéns ursprung" v={data.nulage.ide_ursprung} />
        <KVBlock k="Teamets kompetenser" v={data.nulage.kompetenser} />
        <KVBlock k="Status IP" v={data.nulage.ip_status} />
        <KVBlock k="Utvecklingsstatus" v={data.nulage.utveckling} />
        <KVBlock k="Time to market (MVP)" v={data.nulage.time_to_market} />
        <KVBlock k="Behov" v={data.nulage.behov} />
      </ReportSection>

      <ReportSection title="Readiness Levels">
        <table className="w-full text-xs border border-neutral-300">
          <thead className="bg-neutral-100">
            <tr>
              <th className="text-left p-2 w-16">Typ</th>
              <th className="text-left p-2 w-24">Nivå</th>
              <th className="text-left p-2">Kommentar</th>
            </tr>
          </thead>
          <tbody>
            {(["crl", "brl", "tmrl", "srl"] as const).map((k) => (
              <tr key={k} className="border-t border-neutral-300">
                <td className="p-2 font-medium">{k.toUpperCase()}</td>
                <td className="p-2">{data.readiness[k].level || "—"}</td>
                <td className="p-2">{data.readiness[k].comment || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportSection>

      <ReportSection title="Bedömningskriterier — Affärspotential">
        <table className="w-full text-xs border border-neutral-300">
          <thead className="bg-neutral-100">
            <tr>
              <th className="text-left p-2">Kriterium</th>
              <th className="text-left p-2 w-20">Värde (0-3)</th>
              <th className="text-left p-2">Motivering</th>
            </tr>
          </thead>
          <tbody>
            {(
              [
                ["Innovativt", "innovativt"],
                ["Hållbarhetsdriven affär", "hallbar"],
                ["Skalbarhet i affärsmodellen", "skalbar"],
                ["Unika kunskapstillgångar", "unika_kunskaper"],
                ["Person/teambedömning", "team_bedomning"],
              ] as const
            ).map(([label, k]) => (
              <tr key={k} className="border-t border-neutral-300 align-top">
                <td className="p-2 font-medium">{label}</td>
                <td className="p-2">{data.bedomning[k].value || "—"}</td>
                <td className="p-2 whitespace-pre-wrap">{data.bedomning[k].motivation || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportSection>

      <CategoryReport title="Företagskategori" rows={[
        ["Kunskapsintensivt företag", data.kategori.kunskapsintensivt],
        ["Hi-tech företag", data.kategori.hi_tech],
        ["Deeptech företag", data.kategori.deeptech],
        ["Ingen av dessa kategorier", data.kategori.ingen],
      ]} />

      <CategoryReport title="Vinnovas riktlinjer" rows={[
        ["Digital konsult-/tjänsteaffär", data.vinnova.digital_konsult],
        ["Affärskoncept utan tydlig teknisk lösning", data.vinnova.affarskoncept_utan_teknik],
        ["Teknikkoncept utan tydlig affär", data.vinnova.teknikkoncept_utan_affar],
        ["Genomförandeaffär", data.vinnova.genomforande],
        ["Uppfinnare/utvecklingsbolag", data.vinnova.uppfinnare],
        ["Socialt/samhällsinriktade företag", data.vinnova.socialt],
        ["Spelstudios", data.vinnova.spelstudio],
        ["Mode, artist, etc", data.vinnova.mode_artist],
        ["Ingen av dessa kategorier", data.vinnova.ingen],
      ]} />

      <ReportSection title="Beslut">
        <KVBlock k="Kommentarer" v={data.beslut.kommentar} />
        <KVBlock k="Beslut" v={data.beslut.beslut_text} />
        <KV k="Ansvarig affärscoach" v={data.beslut.affarscoach_namn} />
        <KV k="Kontroll utförd" v={data.beslut.affarscoach_kontroll_datum} />
        <KV
          k="Jäv-kontroll"
          v={`${data.beslut.jav_ok.value || "—"}${data.beslut.jav_ok.comment ? ` — ${data.beslut.jav_ok.comment}` : ""}`}
        />
      </ReportSection>

      <div className="text-[10px] text-neutral-500 border-t border-neutral-300 pt-3">
        Inkubera i Örebro AB · Creative House · Ävtomtagatan 14A · 703 42 Örebro · info@inkubera.se ·
        www.inkubera.se
      </div>
    </div>
  );
}

// =============== Bygg-stenar ===============

function Section({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5 space-y-4">
      <div>
        <h2 className="font-display text-xl">{title}</h2>
        {desc && <p className="text-xs text-muted-foreground mt-1">{desc}</p>}
      </div>
      {children}
    </section>
  );
}

function ReportSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-700 border-b border-neutral-300 pb-1 mb-2">
        {title}
      </h2>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-foreground mb-1">{label}</label>
      {children}
    </div>
  );
}

function TextField({
  label,
  help,
  value,
  onChange,
}: {
  label: string;
  help?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label}>
      {help && <p className="text-xs text-muted-foreground -mt-1 mb-1.5">{help}</p>}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input min-h-24"
      />
    </Field>
  );
}

function YesNoField({
  label,
  help,
  value,
  comment,
  onChange,
  allowNotRelevant,
}: {
  label: string;
  help?: string;
  value: string;
  comment: string;
  onChange: (value: any, comment: string) => void;
  allowNotRelevant?: boolean;
}) {
  return (
    <Field label={label}>
      {help && <p className="text-xs text-muted-foreground -mt-1 mb-1.5">{help}</p>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value, comment)}
        className="input"
      >
        <option value="">— välj —</option>
        <option value="ja">Ja</option>
        <option value="nej">Nej</option>
        {allowNotRelevant && <option value="ej_relevant">Ej relevant</option>}
      </select>
      <textarea
        placeholder="Kommentarer"
        value={comment}
        onChange={(e) => onChange(value, e.target.value)}
        className="input mt-2 min-h-16"
      />
    </Field>
  );
}

function ReadinessRow({
  keyName,
  value,
  onChange,
}: {
  keyName: "crl" | "brl" | "tmrl" | "srl";
  value: { level: string; comment: string };
  onChange: (v: { level: string; comment: string }) => void;
}) {
  const labels = { crl: "Customer Readiness", brl: "Business Readiness", tmrl: "Team Readiness", srl: "Sustainability Readiness" };
  return (
    <Field label={`${keyName.toUpperCase()} — ${labels[keyName]}`}>
      <div className="grid sm:grid-cols-[120px_1fr] gap-2">
        <input
          placeholder="Nivå (1-9)"
          value={value.level}
          onChange={(e) => onChange({ ...value, level: e.target.value })}
          className="input"
        />
        <textarea
          placeholder="Kommentar/dokumentation"
          value={value.comment}
          onChange={(e) => onChange({ ...value, comment: e.target.value })}
          className="input min-h-16"
        />
      </div>
      <details className="text-xs text-muted-foreground mt-1.5">
        <summary className="cursor-pointer">Visa nivåskala</summary>
        <ol className="mt-1.5 space-y-0.5 list-none pl-2">
          {READINESS_DESCRIPTIONS[keyName].map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ol>
      </details>
    </Field>
  );
}

function AssessmentRow({
  label,
  help,
  value,
  onChange,
}: {
  label: string;
  help: string;
  value: { value: string; motivation: string };
  onChange: (v: { value: string; motivation: string }) => void;
}) {
  return (
    <Field label={label}>
      <p className="text-xs text-muted-foreground -mt-1 mb-1.5">{help}</p>
      <div className="grid sm:grid-cols-[120px_1fr] gap-2">
        <input
          placeholder="0-3"
          value={value.value}
          onChange={(e) => onChange({ ...value, value: e.target.value })}
          className="input"
        />
        <textarea
          placeholder="Motivering"
          value={value.motivation}
          onChange={(e) => onChange({ ...value, motivation: e.target.value })}
          className="input min-h-20"
        />
      </div>
    </Field>
  );
}

function CategoryRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: { selected: boolean; motivation: string };
  onChange: (v: { selected: boolean; motivation: string }) => void;
}) {
  return (
    <div className="grid sm:grid-cols-[24px_1fr_2fr] gap-2 items-start">
      <input
        type="checkbox"
        checked={value.selected}
        onChange={(e) => onChange({ ...value, selected: e.target.checked })}
        className="mt-2"
      />
      <div className="text-sm pt-1.5">{label}</div>
      <textarea
        placeholder="Motivering / kommentarer"
        value={value.motivation}
        onChange={(e) => onChange({ ...value, motivation: e.target.value })}
        className="input min-h-12"
      />
    </div>
  );
}

function RecommendationCard({
  rec,
  rules,
}: {
  rec: ReturnType<typeof evaluateRecommendation>;
  rules: AdmissionRules;
}) {
  const noRules = Object.values(rules).every((v) => v === null || v === undefined || v === false);
  const cls =
    rec.decision === "Rekommenderas"
      ? "border-green-500/40 bg-green-500/5"
      : rec.decision === "Rekommenderas med åtgärder"
        ? "border-amber-500/40 bg-amber-500/5"
        : rec.decision === "Ej rekommenderas"
          ? "border-red-500/40 bg-red-500/5"
          : "border-border bg-secondary/40";
  return (
    <div className={`rounded-2xl border p-5 ${cls}`}>
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="size-4" />
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          Automatisk rekommendation
        </div>
      </div>
      <div className="font-display text-2xl mb-2">{rec.decision}</div>
      {noRules && (
        <p className="text-xs text-muted-foreground">
          Inga antagningskriterier är konfigurerade ännu. En admin kan lägga till tröskelvärden i databasen
          (tabellen <code className="text-foreground">prepare_admission_criteria</code>) så börjar
          rekommendationen ta hänsyn till dem automatiskt.
        </p>
      )}
      {rec.reasons.length > 0 && (
        <div className="mt-2">
          <div className="text-xs font-medium text-muted-foreground mb-1">Saknade krav:</div>
          <ul className="text-sm list-disc pl-5 space-y-0.5">
            {rec.reasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}
      {rec.passed.length > 0 && (
        <div className="mt-2">
          <div className="text-xs font-medium text-muted-foreground mb-1">Uppfyllda krav:</div>
          <ul className="text-sm list-disc pl-5 space-y-0.5 text-muted-foreground">
            {rec.passed.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RecBlock({ rec }: { rec: ReturnType<typeof evaluateRecommendation> }) {
  return (
    <div className="border border-neutral-300 rounded-lg p-4 bg-neutral-50">
      <div className="text-[11px] uppercase tracking-wider text-neutral-500">Rekommendation</div>
      <div className="text-lg font-semibold mt-0.5">{rec.decision}</div>
      {rec.reasons.length > 0 && (
        <div className="mt-2 text-xs">
          <div className="font-medium">Saknade krav:</div>
          <ul className="list-disc pl-5">{rec.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function CategoryReport({
  title,
  rows,
}: {
  title: string;
  rows: [string, { selected: boolean; motivation: string }][];
}) {
  const visible = rows.filter(([, v]) => v.selected || v.motivation);
  if (visible.length === 0) return null;
  return (
    <ReportSection title={title}>
      <table className="w-full text-xs border border-neutral-300">
        <thead className="bg-neutral-100">
          <tr>
            <th className="text-left p-2 w-12">Vald</th>
            <th className="text-left p-2">Kategori</th>
            <th className="text-left p-2">Motivering</th>
          </tr>
        </thead>
        <tbody>
          {visible.map(([label, v]) => (
            <tr key={label} className="border-t border-neutral-300 align-top">
              <td className="p-2">{v.selected ? "✓" : "—"}</td>
              <td className="p-2">{label}</td>
              <td className="p-2 whitespace-pre-wrap">{v.motivation || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ReportSection>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="grid grid-cols-[200px_1fr] gap-3 text-xs">
      <div className="text-neutral-500">{k}</div>
      <div>{v || "—"}</div>
    </div>
  );
}

function KVBlock({ k, v }: { k: string; v: string }) {
  return (
    <div className="text-xs">
      <div className="text-neutral-500 mb-0.5">{k}</div>
      <div className="whitespace-pre-wrap">{v || "—"}</div>
    </div>
  );
}

function fmtYesNo(o: { value: string; comment: string }) {
  const v = o.value === "ja" ? "Ja" : o.value === "nej" ? "Nej" : o.value === "ej_relevant" ? "Ej relevant" : o.value === "ok" ? "Ok" : o.value === "ej_ok" ? "Ej ok" : "—";
  return `${v}${o.comment ? ` — ${o.comment}` : ""}`;
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === "godkänd"
      ? "bg-green-500/15 text-green-700 dark:text-green-300"
      : status === "avslagen"
        ? "bg-red-500/15 text-red-700 dark:text-red-300"
        : status === "parkerad"
          ? "bg-muted text-muted-foreground"
          : "bg-amber-500/15 text-amber-700 dark:text-amber-300";
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${cls}`}>{status}</span>
  );
}
